import * as React from "react";
import { query } from "./graph";
import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import PendingStatusView from "../PendingStatusView";
import SearchableColorCombobox from "../SearchableColorCombobox";
import Typography from "@mui/material/Typography";

const InkColorModal = ({
  initialInkColors = [],
  initialPantoneColors = [],
  inputErrors,
  isOpen,
  onColorsAdded,
  onRequestClose,
  title
}) => {
  // Query:
  const { data, loading, error } = useQuery(query, {
    skip: !isOpen
  });

  const inkColors = useMemo(() => {
    if (data && data.inkColors) return data.inkColors;
    return [];
  }, [data]);

  const pantoneColors = useMemo(() => {
    if (data && data.pantoneColors) return data.pantoneColors;
    return [];
  }, [data]);

  // States:
  const [selectedInkColors, setSelectedInkColors] = useState(initialInkColors);
  const [selectedPantoneColors, setSelectedPantoneColors] =
    useState(initialPantoneColors);

  // Handlers:
  const handleInkColorModalCancelButtonClick = () => {
    setSelectedInkColors(initialInkColors);
    setSelectedPantoneColors(initialPantoneColors);
    onRequestClose();
  };

  const handleInkColorModalRequestClose = () => {
    setSelectedInkColors(initialInkColors);
    setSelectedPantoneColors(initialPantoneColors);
    onRequestClose();
  };

  const handleFormSubmit = () => {
    if (selectedInkColors === null && selectedPantoneColors === null) {
      onRequestClose();
    } else {
      onColorsAdded(selectedInkColors, selectedPantoneColors);
    }
  };

  const handleInkColorsComboboxSelect = selection => {
    const selectedInkColors = inkColors.filter(
      ({ id }) => selection.indexOf(id) !== -1
    );
    setSelectedInkColors(selectedInkColors);
  };

  const handlePantoneColorsComboboxSelect = selection => {
    const selectedPantoneColors = pantoneColors.filter(
      ({ id }) => selection.indexOf(id) !== -1
    );
    setSelectedPantoneColors(selectedPantoneColors);
  };

  return (
    <Dialog
      aria-describedby="Ink Color Modal"
      aria-labelledby="InkColorModal"
      open={isOpen}
      onClose={handleInkColorModalRequestClose}
      fullWidth={true}
    >
      <DialogTitle>{title ? title : "Add Ink Colors"}</DialogTitle>
      <DialogContent>
        {!data ? (
          <PendingStatusView status="Waiting" />
        ) : loading ? (
          <PendingStatusView status="Loading" />
        ) : error ? (
          <Typography color="error" variant="body2">
            {error.message}
          </Typography>
        ) : !data.inkColors && !data.pantoneColors ? (
          <Typography color="error" variant="body2">
            There are no colors available
          </Typography>
        ) : inputErrors.length > 0 ? (
          <Typography color="error" variant="body2">
            {inputErrors.join(", ")}
          </Typography>
        ) : (
          <React.Fragment>
            <SearchableColorCombobox
              onSelect={handleInkColorsComboboxSelect}
              placeholder="Search Ink Colors"
              colorOptions={data.inkColors.map(({ name: label, ...props }) => ({
                ...props,
                label
              }))}
              multiple
            />
            <SearchableColorCombobox
              onSelect={handlePantoneColorsComboboxSelect}
              placeholder="Search Pantone Colors"
              colorOptions={data.pantoneColors.map(
                ({ code: label, ...props }) => ({
                  ...props,
                  label
                })
              )}
              multiple
            />
            <DialogActions>
              <Button
                color="secondary"
                onClick={handleInkColorModalCancelButtonClick}
              >
                Cancel
              </Button>
              <Button color="primary" onClick={handleFormSubmit}>
                Save
              </Button>
            </DialogActions>
          </React.Fragment>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InkColorModal;
